import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, Tooltip, Typography
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { StatusChip } from '../../Documents/DocumentsTable';
import { fmtDateTime } from '../../Documents/format';
import { PAGE_SIZES } from '../constants';
import { MarkingPagination, MarkingRow, SortField, SortState } from '../types';

interface Props {
  rows: MarkingRow[];
  pagination: MarkingPagination;
  loading: boolean;
  error: string | null;
  sort: SortState;
  hasFilters: boolean;
  onSort: (key: SortField) => void;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
  onOpenDocument: (documentId: number) => void;
  onResetFilters: () => void;
  onRetry: () => void;
}

const BORDER = '#e2e8f0';
const COPIED_MS = 1800;

const COLUMNS: Array<{ key: SortField | null; label: string; width?: number | string }> = [
  { key: 'code', label: 'Код маркировки' },
  { key: 'product', label: 'Товар', width: '24%' },
  { key: 'type', label: 'Тип', width: 150 },
  { key: 'status', label: 'Статус накладной', width: 190 },
  { key: 'document', label: '№ док.', width: 100 },
  { key: null, label: 'Откуда → Куда', width: '18%' },
  { key: 'date', label: 'Дата записи', width: 140 }
];

const MarkingCodesTable: React.FC<Props> = ({
  rows, pagination, loading, error, sort, hasFilters,
  onSort, onPage, onLimit, onOpenDocument, onResetFilters, onRetry
}) => {
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback((code: string) => {
    const done = () => {
      setCopied(code);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(null), COPIED_MS);
    };
    try {
      navigator.clipboard.writeText(code).then(done, () => undefined);
    } catch {
      // буфер обмена недоступен (нет https / нет разрешения) - просто не подтверждаем копирование
    }
  }, []);

  const showError = Boolean(error) && !rows.length;
  const showEmpty = !loading && !error && !rows.length;

  return (
    <Paper variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      {error && (
        <Alert severity="error" sx={{ m: 1.5 }} action={<Button color="inherit" size="small" onClick={onRetry}>Повторить</Button>}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)', minHeight: 240 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 1100, opacity: loading && rows.length ? 0.6 : 1, transition: 'opacity .15s' }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell
                  key={col.label} sortDirection={col.key && sort.sortBy === col.key ? sort.sortOrder : false}
                  sx={{ width: col.width, fontWeight: 700, fontSize: 12, color: '#475569', bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' }}
                >
                  {col.key ? (
                    <TableSortLabel
                      active={sort.sortBy === col.key} direction={sort.sortBy === col.key ? sort.sortOrder : 'asc'}
                      onClick={() => onSort(col.key as SortField)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9', py: 0.9 } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12.5, fontWeight: 500, wordBreak: 'break-all' }}>
                      {row.code}
                    </Typography>
                    <Tooltip title={copied === row.code ? 'Скопировано' : 'Скопировать код'}>
                      <IconButton size="small" onClick={() => copy(row.code)} aria-label="Скопировать код" sx={{ flexShrink: 0 }}>
                        {copied === row.code
                          ? <CheckRoundedIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          : <ContentCopyRoundedIcon sx={{ fontSize: 16, color: '#94a3b8' }} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2" title={row.product}
                    sx={{ fontSize: 13, fontWeight: 500, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {row.product || '-'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 13, color: '#475569' }}>{row.typeName || '-'}</TableCell>
                <TableCell><StatusChip status={row.status} name={row.statusName} /></TableCell>
                <TableCell>
                  <Typography
                    variant="body2" onClick={() => onOpenDocument(row.documentId)}
                    sx={{
                      display: 'inline-block', px: 0.6, py: 0.2, borderRadius: 1, fontFamily: 'monospace', fontWeight: 700,
                      color: 'primary.main', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover', textDecoration: 'underline' }
                    }}
                  >
                    #{row.documentId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontSize: 12.5, color: '#64748b' }}>{row.sender || '-'}</Typography>
                    <ArrowForwardRoundedIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                    <Typography variant="body2" sx={{ fontSize: 12.5, fontWeight: 600 }}>{row.recipient || '-'}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap', color: '#475569' }}>{fmtDateTime(row.date)}</TableCell>
              </TableRow>
            ))}

            {showError && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body2">Данные не загружены</Typography>
                </TableCell>
              </TableRow>
            )}

            {showEmpty && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body2">{hasFilters ? 'По заданным фильтрам кодов маркировки не найдено' : 'Кодов маркировки нет'}</Typography>
                  {hasFilters && (
                    <Typography
                      variant="body2" onClick={onResetFilters}
                      sx={{ mt: 0.5, color: 'primary.main', cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Сбросить фильтры
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div" count={pagination.total} page={Math.max(0, pagination.page - 1)} rowsPerPage={pagination.limit}
        rowsPerPageOptions={PAGE_SIZES}
        onPageChange={(_, p) => onPage(p + 1)}
        onRowsPerPageChange={e => onLimit(Number(e.target.value))}
        labelRowsPerPage="На странице:"
        labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString('ru-RU')}–${to.toLocaleString('ru-RU')} из ${count.toLocaleString('ru-RU')}`}
        sx={{ borderTop: `1px solid ${BORDER}` }}
      />
    </Paper>
  );
};

export default React.memo(MarkingCodesTable);
